import { supabase } from '../supabaseClient';
import { ThumbnailCard } from '../utils/feedGrid';
import { ThemeId } from '../constants/themes';

interface FeedThumbnailRow {
  id: string;
  batch_id: string;
  title: string;
  image_url: string;
  channel_name: string;
  channel_username: string | null;
  channel_avatar_url: string | null;
}

const toCard = (row: FeedThumbnailRow): ThumbnailCard => ({
  kind: 'thumbnail',
  id: row.id,
  batchId: row.batch_id,
  title: row.title,
  imageUrl: row.image_url,
  channelName: row.channel_name,
  channelUsername: row.channel_username,
  channelAvatarUrl: row.channel_avatar_url,
});

/** Au plus une miniature par lot du thème, la moins vue de son lot. */
export const fetchFeedThumbnails = async (
  themeId: ThemeId,
  limit: number,
  excludeBatchIds: string[],
): Promise<ThumbnailCard[]> => {
  const { data, error } = await supabase.rpc('get_feed_thumbnails', {
    p_theme_id: themeId,
    p_limit: limit,
    p_exclude_batch_ids: excludeBatchIds,
  });

  if (error) throw error;

  return ((data as FeedThumbnailRow[]) || []).map(toCard);
};

/** Accueil : au plus une miniature par lot, tous thèmes confondus. */
export const fetchHomeThumbnails = async (
  limit: number,
  excludeBatchIds: string[],
): Promise<ThumbnailCard[]> => {
  const { data, error } = await supabase.rpc('get_home_thumbnails', {
    p_limit: limit,
    p_exclude_batch_ids: excludeBatchIds,
  });

  if (error) throw error;

  return ((data as FeedThumbnailRow[]) || []).map(toCard);
};

/** Nos miniatures dont le titre contient tous les mots de la requête. */
export const searchFeedThumbnails = async (
  query: string,
  limit: number,
  excludeBatchIds: string[],
): Promise<ThumbnailCard[]> => {
  const { data, error } = await supabase.rpc('search_feed_thumbnails', {
    p_query: query,
    p_limit: limit,
    p_exclude_batch_ids: excludeBatchIds,
  });

  if (error) throw error;

  return ((data as FeedThumbnailRow[]) || []).map(toCard);
};

export interface ClickOutcome {
  success: boolean;
  error?: string;
  /** CTR de la miniature cliquée, en %. `null` pour un clic sur un leurre. */
  ctr: number | null;
}

/** Une impression pour chaque miniature qui vient d'apparaître à l'écran. */
export const recordImpressions = async (thumbnailIds: string[]): Promise<void> => {
  if (thumbnailIds.length === 0) return;

  const { error } = await supabase.rpc('record_impressions', {
    p_thumbnail_ids: thumbnailIds,
  });

  if (error) throw error;
};

/** Un clic sur une de nos miniatures. Renvoie son CTR pour l'afficher. */
export const recordClick = async (thumbnailId: string): Promise<ClickOutcome> => {
  const { data, error } = await supabase.rpc('record_click', {
    p_thumbnail_id: thumbnailId,
  });

  if (error) throw error;

  const result = data as { success: boolean; error?: string; ctr: number | null };

  return {
    success: result?.success ?? false,
    error: result?.error,
    ctr: result?.ctr ?? null,
  };
};

export interface ThumbnailStats {
  id: string;
  title: string;
  image_url: string;
  impressions: number;
  clicks: number;
  /** `null` tant qu'aucune impression n'a été enregistrée. */
  ctr: number | null;
}

export interface BatchStats {
  batch_id: string;
  theme_id: string;
  /** CTR moyen du thème, tous créateurs confondus. */
  theme_avg_ctr: number | null;
  total_impressions: number;
  /** `null` tant que le lot n'a aucune impression. */
  winner_thumbnail_id: string | null;
  thumbnails: ThumbnailStats[];
}

/** Statistiques des lots de l'utilisateur connecté, et de lui seul. */
export const fetchCreatorFeedStats = async (): Promise<BatchStats[]> => {
  const { data, error } = await supabase.rpc('get_creator_feed_stats');

  if (error) throw error;

  return (data as BatchStats[]) || [];
};

export interface ThemeInfo {
  id: string;
  batchCount: number;
  /** Requête envoyée à YouTube pour peupler ce thème en leurres. */
  defaultQuery: string;
}

/** Tous les thèmes, avec leur stock et leur requête de leurres. */
export const fetchThemes = async (): Promise<ThemeInfo[]> => {
  const { data, error } = await supabase.rpc('get_themes');

  if (error) throw error;

  const rows = (data as { id: string; batch_count: number; default_query: string }[]) || [];

  return rows.map(row => ({
    id: row.id,
    batchCount: Number(row.batch_count),
    defaultQuery: row.default_query,
  }));
};
